/**
 * CORTES DE LIQUIDACIÓN en la consola (13.4; Mario, 23-oct; COR-01..14 del núcleo). Lógica pura, sin Angular:
 * la clave de una liquidación, la hora y la ventana de un corte, el agrupado por corte, cuándo sondear y si la
 * vista previa deja confirmar. Pruebas: test/liquidacion-cortes.test.mjs.
 */

/** La clave de una liquidación en el cierre: el comercio en el corte 1 (o sin corte), `comercio#n` en los demás. Igual que el núcleo (dominio/cortes.js). */
export const claveDe = (l: any): string => (l && l.corte > 1 ? `${l.merchant}#${l.corte}` : String(l && l.merchant));

/** HH:MM en hora de Venezuela (UTC-4). */
export const horaDelCorte = (d: any): string => (d ? new Date(new Date(d).getTime() - 4 * 3600e3).toISOString().slice(11, 16) : '—');

/** «Corte N» para una tanda o una liquidación; vacío si no trae corte. */
export const etiquetaCorte = (n: any): string => (n ? `Corte ${n}` : '');

/** Las liquidaciones agrupadas por corte (sin corte = corte 1), en orden, con el encabezado «Corte N · HH:MM» (la hora en que terminó el corte). */
export const agruparPorCorte = (liquidaciones: any[] | null | undefined, cortes: any[] | null | undefined): { n: number; titulo: string; liquidaciones: any[] }[] => {
  const por = new Map<number, any[]>();
  for (const l of liquidaciones || []) { const n = Number(l.corte) || 1; if (!por.has(n)) por.set(n, []); por.get(n)!.push(l); }
  return [...por.keys()].sort((a, b) => a - b).map((n) => {
    const c = (cortes || []).find((x: any) => x.n === n);
    return { n, titulo: c && c.hasta ? `Corte ${n} · ${horaDelCorte(c.hasta)}` : `Corte ${n}`, liquidaciones: por.get(n)! };
  });
};

/** La ventana de un corte para la tabla: «09:05–14:40»; sin `desde`, «hasta las 09:05». */
export const ventanaDelCorte = (c: any): string => {
  if (c.desde && c.hasta) return `${horaDelCorte(c.desde)}–${horaDelCorte(c.hasta)}`;
  if (c.hasta) return `hasta las ${horaDelCorte(c.hasta)}`;
  if (c.desde) return `desde las ${horaDelCorte(c.desde)}`;
  return '—';
};

/** La vista previa: «Desde las 09:05 (corte 1) hasta ahora». */
export const textoVentanaPrevia = (v: any): string => {
  if (!v) return '';
  if (!v.desde) return 'Desde el último corte hasta ahora';
  return `Desde las ${horaDelCorte(v.desde)}${v.siguiente > 1 ? ` (corte ${v.siguiente - 1})` : ''} hasta ahora`;
};

/** Solo se consulta el avance mientras haya un corte EN_CURSO. */
export const debeSondear = (cortes: any[] | null | undefined): boolean => (cortes || []).some((c: any) => c.estado === 'EN_CURSO');

/** La vista previa deja confirmar solo si hay algo que cortar y nada lo bloquea. */
export const puedeConfirmarPrevia = (v: any): boolean => !!v && !v.vacio && !v.bloqueo;

/** El total del día de los cortes del resumen (los que no terminaron no suman). */
export const totalDeCortes = (cortes: any[] | null | undefined): { liquidaciones: number; neto_usd: number } => {
  const ok = (cortes || []).filter((c: any) => !c.estado || c.estado === 'LISTO');
  return { liquidaciones: ok.reduce((s: number, c: any) => s + (Number(c.liquidaciones) || 0), 0), neto_usd: Math.round(ok.reduce((s: number, c: any) => s + (Number(c.neto_usd) || 0), 0) * 100) / 100 };
};

/** El avance de un corte en curso, en palabras. */
export const textoAvance = (c: any): string => {
  const a = c && c.avance;
  if (!a || !a.total) return 'Calculando…';
  return `Calculando · ${a.hechos || 0} de ${a.total} comercios${a.comercio ? ` (${a.comercio})` : ''}`;
};

/** Mismo plazo que el núcleo (dominio/cortes.js, LATIDO_MS): un corte en curso sin latido en 10 minutos se da por caído. */
export const LATIDO_MS = 10 * 60 * 1000;
export const sinLatido = (c: any, ahora: Date = new Date()): boolean => !!c && c.estado === 'EN_CURSO' && !!c.latido && ahora.getTime() - new Date(c.latido).getTime() >= LATIDO_MS;

/** El corte (o los cortes) de una tanda: «Corte 2», «Cortes 1 y 2», «Cortes 1, 2 y 3»; vacío si no se sabe. */
export const etiquetaCortes = (ns: any): string => {
  const u = [...new Set((Array.isArray(ns) ? ns : [ns]).map(Number).filter((n: number) => n > 0))].sort((a, b) => a - b);
  if (!u.length) return '';
  if (u.length === 1) return `Corte ${u[0]}`;
  return `Cortes ${u.slice(0, -1).join(', ')} y ${u[u.length - 1]}`;
};

/** COR-10: «2 comercios» o, si hay más liquidaciones que comercios (varios cortes), «2 comercios · 4 liquidaciones». */
export const textoConteo = (c: { comercios?: number; liquidaciones?: number } | null | undefined): string => {
  const n = Number(c && c.comercios) || 0, m = Number(c && c.liquidaciones) || 0;
  return `${n} ${n === 1 ? 'comercio' : 'comercios'}${m > n ? ` · ${m} liquidaciones` : ''}`;
};
